import type { SessionReadiness, SessionSync } from "@calega/realtime";
import type { Socket } from "socket.io-client";

type Rejection = { ok: false; error: { code: string; message: string } };

/** Repairs session membership and acknowledgements, never replays exercise commands. */
export function createSessionRecovery(socket: Socket, sessionId: string, callbacks: {
  revision(): number;
  applySync(sync: SessionSync): void;
  connected(value: boolean): void;
  ready(value: boolean): void;
  notice(message: string): void;
}) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let generation = 0;
  let stopped = false;
  let inFlight = false;
  let synchronized = false;
  let attempts = 0;
  let retryNotBefore = 0;
  let lastWake = -Infinity;

  function clearTimer() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function schedule(delay = Math.min(30_000, 6_000 * 2 ** Math.min(attempts, 3))) {
    if (stopped || inFlight || timer !== null || !socket.connected) return;
    timer = setTimeout(() => { timer = null; recover(); }, Math.max(delay, retryNotBefore - Date.now()));
  }

  function invalidate() {
    if (stopped) return;
    synchronized = false;
    callbacks.ready(false);
    schedule();
  }

  function stop(message: string) {
    stopped = true;
    generation += 1;
    clearTimer();
    callbacks.connected(false);
    callbacks.ready(false);
    socket.disconnect();
    callbacks.notice(message);
  }

  function recover(delay = 0) {
    if (stopped) return;
    if (delay > 0) {
      retryNotBefore = Math.max(retryNotBefore, Date.now() + delay);
      clearTimer();
      callbacks.connected(false);
      callbacks.ready(false);
    }
    if (stopped || inFlight || !socket.connected) return;
    if (Date.now() < retryNotBefore) { schedule(retryNotBefore - Date.now()); return; }
    clearTimer();
    inFlight = true;
    synchronized = false;
    callbacks.connected(false);
    callbacks.ready(false);
    const currentGeneration = generation;
    const current = () => !stopped && socket.connected && currentGeneration === generation;
    const failed = (response?: Rejection) => {
      if (!current()) return;
      inFlight = false;
      callbacks.ready(false);
      synchronized = false;
      attempts += 1;
      if (response?.error.code === "forbidden" || response?.error.code === "invalid_payload") {
        stop(response.error.message);
        return;
      }
      callbacks.notice("Die Sitzung wird automatisch wiederhergestellt. Bitte kurz warten.");
      if (response?.error.code === "rate_limited") retryNotBefore = Date.now() + 12_000;
      schedule(Math.min(30_000, 1_000 * 2 ** Math.min(attempts - 1, 5)));
    };
    socket.timeout(10_000).emit("session:join", { sessionId, afterRevision: callbacks.revision(), whiteboardVersion: 1 },
      (error: Error | null, response?: SessionSync | Rejection) => {
        if (!current()) return;
        if (error || !response) return failed();
        if ("ok" in response) return failed(response);
        callbacks.applySync(response);
        socket.timeout(10_000).emit("session:ready", { sessionId, revision: callbacks.revision() },
          (readyError: Error | null, readyResponse?: { ok: true } | Rejection) => {
            if (!current()) return;
            if (readyError || !readyResponse) return failed();
            if (!readyResponse.ok) return failed(readyResponse);
            inFlight = false;
            if (Date.now() < retryNotBefore) { schedule(retryNotBefore - Date.now()); return; }
            callbacks.connected(true);
            callbacks.notice("");
            if (!synchronized) {
              attempts += 1;
              schedule();
            }
          });
      });
  }

  function onConnect() {
    generation += 1;
    inFlight = false;
    attempts = 0;
    callbacks.connected(false);
    recover();
  }

  function onDisconnect(reason: string) {
    generation += 1;
    inFlight = false;
    synchronized = false;
    clearTimer();
    callbacks.connected(false);
    callbacks.ready(false);
    if (!stopped && reason === "io server disconnect") {
      stop("Die Verbindung wurde vom Server beendet. Bitte die Anmeldung prüfen und der Sitzung erneut beitreten.");
    }
  }

  function onConnectError() {
    if (stopped) return;
    if (!socket.active) {
      stop("Die Verbindung wurde abgewiesen. Bitte die Anmeldung prüfen und der Sitzung erneut beitreten.");
      return;
    }
    callbacks.connected(false);
    callbacks.ready(false);
    callbacks.notice("Die Verbindung konnte nicht hergestellt werden. Die Sitzung wartet auf die Wiederverbindung.");
  }

  function onReadiness(status: SessionReadiness) {
    if (stopped || status.sessionId !== sessionId || status.revision < callbacks.revision()) return;
    synchronized = status.ready && status.revision === callbacks.revision();
    callbacks.ready(synchronized);
    if (synchronized) {
      attempts = 0;
      if (Date.now() >= retryNotBefore) clearTimer();
    } else {
      schedule();
    }
  }

  socket.on("connect", onConnect);
  socket.on("disconnect", onDisconnect);
  socket.on("connect_error", onConnectError);
  socket.on("session:readiness", onReadiness);

  function wake() {
    if (stopped || inFlight || Date.now() - lastWake < 1_000 || typeof navigator !== "undefined" && navigator.onLine === false) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    lastWake = Date.now();
    if (socket.connected) recover();
    else if (socket.active) socket.connect();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("online", wake);
    window.addEventListener("pageshow", wake);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
  }

  return {
    invalidate,
    recover,
    dispose() {
      stopped = true;
      generation += 1;
      clearTimer();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("session:readiness", onReadiness);
      if (typeof window !== "undefined") {
        window.removeEventListener("online", wake);
        window.removeEventListener("pageshow", wake);
        window.removeEventListener("focus", wake);
        document.removeEventListener("visibilitychange", wake);
      }
    },
  };
}
